// ======================================================
// FILE: TicketSelectorPage.js
// LOCATION: src/pages/dashboard_pages/weighbridge/TicketSelectorPage.js
// ======================================================
//
// PURPOSE:
// - Show list of unused weighbridge tickets (Wayware integration)
// - Styled to match UAG list pages (Goods Received format)
//
// STATUS:
// - Backend not ready yet -> uses mock tickets
//
// ======================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import {
  Block,
  BlockHead,
  BlockBetween,
  BlockDes,
  BlockHeadContent,
  BlockTitle,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
  Button as UagButton,
} from "../../../components/Component";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Alert, Badge, Modal, ModalBody, ModalHeader, Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import {
  RETRIEVE_ALL_WEIGH_BRIDGE_TICKETS_API,
  RETRIEVE_WEIGH_BRIDGE_TICKET_DETAILS_API,
} from "../../../api/api_data";
import { makeEmployeeDashboardApiRequest } from "../../../api/api_helper";
import AuthLoader from "../../components/page_messaging/AuthLoader";
import UAGLogo from "../../../images/logo.png";
import {
  Document as PdfDocument,
  Page as PdfPage,
  Text as PdfText,
  View as PdfView,
  Image as PdfImage,
  StyleSheet as PdfStyleSheet,
  pdf,
} from "@react-pdf/renderer";
import usePersistentState from "../../../Hooks/usePersistentState";
import {
  syncWeighbridgeTicketsFromAgent,
  updateWeighbridgeTicketFromAgent,
} from "../../../services/weighbridge_sync_service";
import { WeighbridgeService } from "../../../services/weighbridge_service";
import { success_sign_out_modal } from "../../../reducers/dashboard_reducers/auth/sign_out_modal_Slice";
import {
  pickWeighbridgeValue,
  resolveWeighbridgeTicketRecord,
  resolveWeighbridgeTicketPrefillData,
  resolveWeighbridgeUsageStatus,
  sortWeighbridgeTicketsDesc,
} from "../../../utils/weighbridgeTicketNormalization";
import { formatWeighbridgeWeightMt } from "../../../utils/weighbridgeUtils";
import {
  matchesWeighbridgeTicketDateFilter,
  WEIGHBRIDGE_DATE_FILTERS,
} from "../../../utils/weighbridgeTicketDateFilters";
import * as app_consts from "../../../constants/react_app_consts";
import { messageToast } from "../../../utils/Utils";

const getDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  return value;
};

const isPresent = (value) => value !== null && value !== undefined && value !== "";

const pickFirstPresent = (...values) => values.find((value) => isPresent(value)) || "";

const WEIGHBRIDGE_WEIGHT_UNIT = "MT";

const formatTicketStatus = (value) => {
  const normalizedValue = getDisplayValue(value);
  if (normalizedValue === "--") return normalizedValue;
  if (String(normalizedValue).toUpperCase() === "GOODS_RECEIVED_NOTE") return "GOODS RECEIVED";
  return String(normalizedValue).replaceAll("_", " ");
};

const formatDesignation = (value) => {
  const normalizedValue = getDisplayValue(value);
  if (normalizedValue === "--") return normalizedValue;
  return String(normalizedValue).replaceAll("_", " ");
};

const normalizeTicketDesignation = (value) => {
  const normalizedValue = getDisplayValue(value);
  if (normalizedValue === "--") return "NOT SET";
  const cleanedValue = String(normalizedValue).trim().toUpperCase();
  return cleanedValue || "NOT SET";
};

const normalizeTicketStatusForWorkflow = (value) =>
  String(value || "")
    .trim()
    .replaceAll("_", " ")
    .toLowerCase();

const canStartWorkflowFromTicket = (ticket = {}, expectedDesignation = "NOT SET", allowedStatuses = []) => {
  const usageStatus = String(ticket?.usage_status || "").toUpperCase();
  const designation = normalizeTicketDesignation(ticket?.designation);
  const normalizedStatus = normalizeTicketStatusForWorkflow(ticket?.weigh_bridge_ticket_status);

  if (usageStatus === "USED") {
    return false;
  }

  if (designation !== expectedDesignation) {
    return false;
  }

  if (!allowedStatuses.length) {
    return true;
  }

  return allowedStatuses.some((status) => normalizeTicketStatusForWorkflow(status) === normalizedStatus);
};

const canStartGoodsReceivedWorkflowFromTicket = (ticket = {}) =>
  canStartWorkflowFromTicket(ticket, "NOT SET", ["offloading goods", ""]);

const getTicketStatusTone = (value) => {
  const normalizedValue = String(value || "").toLowerCase();
  if (
    normalizedValue.includes("complete") ||
    normalizedValue.includes("delivered") ||
    normalizedValue.includes("received")
  )
  {
    return "success";
  }

  if (
    normalizedValue.includes("processing") ||
    normalizedValue.includes("transit") ||
    normalizedValue.includes("offloading") ||
    normalizedValue.includes("onboarding")
  )
  {
    return "warning";
  }

  return "secondary";
};

const formatDateValue = (iso) => {
  if (!iso) return "--";
  try
  {
    return new Date(iso).toLocaleString();
  } catch
  {
    return "--";
  }
};

const formatWeightValue = (value) =>
  formatWeighbridgeWeightMt(value, { unit: WEIGHBRIDGE_WEIGHT_UNIT });

const ticketDetailPanelStyle = {
  border: "1px solid #e5e9f2",
  borderRadius: "16px",
  background: "#ffffff",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
};

const ticketDetailHeroStyle = {
  border: "1px solid #dbe4f0",
  borderRadius: "18px",
  background: "linear-gradient(135deg, #f7fbff 0%, #eef5ff 50%, #ffffff 100%)",
  boxShadow: "0 18px 40px rgba(43, 86, 154, 0.10)",
  minHeight: "190px",
};

const ticketModalBrandHeaderStyle = {
  borderBottom: "1px solid #e9eff7",
  paddingBottom: "1rem",
  marginBottom: "1rem",
};

const ticketMetricCardStyle = {
  border: "1px solid #e6edf5",
  borderRadius: "14px",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.05)",
  minHeight: "132px",
};

const ticketSectionCardStyle = {
  ...ticketDetailPanelStyle,
  padding: "1rem 1.1rem",
  height: "100%",
};

const ticketFieldRowStyle = {
  padding: "0.72rem 0",
  borderBottom: "1px solid #eef2f7",
};

const TicketMetricCard = ({ label, value, tone = "primary" }) => (
  <div className="p-3 h-100" style={ticketMetricCardStyle}>
    <div className="text-soft text-uppercase fw-medium small mb-2" style={{ lineHeight: 1.4 }}>
      {label}
    </div>
    <div
      className={`fw-bold fs-3 text-${tone}`}
      style={{ lineHeight: 1.15, overflowWrap: "anywhere" }}
    >
      {getDisplayValue(value)}
    </div>
  </div>
);

const TicketDetailSection = ({ title, subtitle, children }) => (
  <div style={ticketSectionCardStyle}>
    <div className="mb-3">
      <h6 className="mb-1">{title}</h6>
      {subtitle ? <p className="text-soft small mb-0">{subtitle}</p> : null}
    </div>
    {children}
  </div>
);

const TicketDetailField = ({ label, value, valueClassName = "", isLast = false }) => (
  <div
    className="d-flex justify-content-between align-items-start gap-3"
    style={{
      ...ticketFieldRowStyle,
      borderBottom: isLast ? "0" : ticketFieldRowStyle.borderBottom,
      paddingBottom: isLast ? 0 : ticketFieldRowStyle.paddingBottom,
    }}
  >
    <span className="text-soft text-uppercase fw-medium small">{label}</span>
    <span className={`fw-semibold text-end ${valueClassName}`}>{getDisplayValue(value)}</span>
  </div>
);

const getTicketToneHex = (tone = "secondary") => {
  const toneMap = {
    success: "#0f9d58",
    warning: "#f59e0b",
    primary: "#2563eb",
    info: "#0ea5e9",
    secondary: "#64748b",
    dark: "#0f172a",
  };

  return toneMap[tone] || toneMap.secondary;
};

const escapeTicketHtml = (value) =>
  String(getDisplayValue(value))
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const ticketPdfStyles = PdfStyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    color: "#111827",
    backgroundColor: "#f8fafc",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  logo: {
    width: 72,
    height: 40,
    objectFit: "contain",
  },
  headerTextWrap: {
    flex: 1,
    marginLeft: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 3,
  },
  subtitle: {
    fontSize: 10,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  dateText: {
    fontSize: 9,
    fontWeight: 600,
    textAlign: "right",
    width: 150,
    color: "#475569",
  },
  hero: {
    borderWidth: 1,
    borderColor: "#dbe4f0",
    backgroundColor: "#f7fbff",
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  heroRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  heroLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  heroValue: {
    fontSize: 18,
    fontWeight: 700,
    color: "#0f172a",
  },
  heroSubValue: {
    fontSize: 10,
    color: "#475569",
    marginTop: 4,
  },
  badgeRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 10,
  },
  badge: {
    borderRadius: 10,
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 8,
    paddingRight: 8,
    marginRight: 6,
    marginBottom: 6,
  },
  badgeText: {
    fontSize: 8,
    color: "#ffffff",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  metricRow: {
    flexDirection: "row",
    marginBottom: 12,
  },
  metricCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    backgroundColor: "#ffffff",
    padding: 12,
    minHeight: 80,
  },
  metricGap: {
    width: 10,
  },
  metricLabel: {
    fontSize: 8,
    color: "#64748b",
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  metricValue: {
    fontSize: 16,
    fontWeight: 700,
  },
  sectionGrid: {
    flexDirection: "row",
    marginBottom: 12,
  },
  sectionGap: {
    width: 10,
  },
  sectionCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 12,
    backgroundColor: "#ffffff",
    padding: 12,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: 2,
  },
  sectionSubtitle: {
    fontSize: 8,
    color: "#64748b",
    marginBottom: 8,
  },
  fieldRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingTop: 7,
    paddingBottom: 7,
    borderBottomWidth: 1,
    borderColor: "#eef2f7",
  },
  fieldRowLast: {
    borderBottomWidth: 0,
  },
  fieldLabel: {
    fontSize: 8,
    color: "#64748b",
    textTransform: "uppercase",
    fontWeight: 700,
    width: "38%",
  },
  fieldValue: {
    fontSize: 9,
    fontWeight: 600,
    textAlign: "center",
    color: "#0f172a",
    width: "62%",
  },
  poweredBy: {
    marginTop: 8,
    textAlign: "right",
    fontSize: 9,
    color: "#64748b",
    fontWeight: 600,
  },
});

const PdfMetricCard = ({ label, value, tone = "secondary" }) => (
  <PdfView style={ticketPdfStyles.metricCard}>
    <PdfText style={ticketPdfStyles.metricLabel}>{label}</PdfText>
    <PdfText style={[ticketPdfStyles.metricValue, { color: getTicketToneHex(tone) }]}>
      {getDisplayValue(value)}
    </PdfText>
  </PdfView>
);

const PdfFieldRow = ({ label, value, isLast = false }) => (
  <PdfView style={[ticketPdfStyles.fieldRow, isLast ? ticketPdfStyles.fieldRowLast : null]}>
    <PdfText style={ticketPdfStyles.fieldLabel}>{label}</PdfText>
    <PdfText style={ticketPdfStyles.fieldValue}>{getDisplayValue(value)}</PdfText>
  </PdfView>
);

const TicketPdfSection = ({ title, subtitle, fields = [] }) => (
  <PdfView style={ticketPdfStyles.sectionCard}>
    <PdfText style={ticketPdfStyles.sectionTitle}>{title}</PdfText>
    {subtitle ? <PdfText style={ticketPdfStyles.sectionSubtitle}>{subtitle}</PdfText> : null}
    {fields.map((field, index) => (
      <PdfFieldRow
        key={`${title}-${field.label}-${index}`}
        label={field.label}
        value={field.value}
        isLast={index === fields.length - 1}
      />
    ))}
  </PdfView>
);

const TicketPdfDocument = ({ ticket, linkedDocumentFields = [], consignerType = "---" }) => {
  const ticketStatusTone = getTicketStatusTone(ticket?.weigh_bridge_ticket_status);
  const overviewFields = [
    { label: "Reference Number", value: ticket?.weigh_bridge_ticket_reference_no },
    { label: "Ticket Number", value: ticket?.weigh_bridge_ticket_number },
    { label: "External Reference", value: ticket?.external_weigh_bridge_ticket_number },
    { label: "Transaction Type", value: ticket?.weigh_bridge_ticket_type },
    { label: "Created On", value: formatDateValue(ticket?.created_on || ticket?.time_in) },
    { label: "Source System", value: ticket?.source_system },
    { label: "Sync Batch", value: ticket?.sync_batch_id },
    { label: "Comments", value: ticket?.comments },
  ];
  const vehicleFields = [
    { label: "Truck Reg No", value: ticket?.truck_licence_plate },
    { label: "Truck Driver", value: ticket?.truck_driver_name },
    { label: "Transporter", value: ticket?.transporter_name },
    { label: "Trailer One", value: ticket?.trailer_one },
    { label: "Trailer Two", value: ticket?.trailer_two },
  ];
  const weightFields = [
    { label: "Tare Weight", value: formatWeightValue(ticket?.first_weight) },
    { label: "Gross Weight", value: formatWeightValue(ticket?.second_weight) },
    { label: "Net Weight", value: formatWeightValue(ticket?.net_weight) },
    { label: "Time In", value: formatDateValue(ticket?.time_in) },
    { label: "Time Out", value: formatDateValue(ticket?.time_out) },
  ];
  const resolvedLinkedFields = linkedDocumentFields?.length
    ? linkedDocumentFields
    : [
        { label: "Designation", value: formatDesignation(ticket?.designation) },
        { label: "Consigner Type", value: consignerType },
      ];

  return (
  <PdfDocument title={ticket?.weigh_bridge_ticket_reference_no || ticket?.weigh_bridge_ticket_number || "Weighbridge Ticket"}>
    <PdfPage size="A4" style={ticketPdfStyles.page}>
      <PdfView style={ticketPdfStyles.header}>
        <PdfImage src={UAGLogo} style={ticketPdfStyles.logo} />
        <PdfView style={ticketPdfStyles.headerTextWrap}>
          <PdfText style={ticketPdfStyles.title}>Weighbridge Ticket</PdfText>
          <PdfText style={ticketPdfStyles.subtitle}>United African Grain</PdfText>
        </PdfView>
        <PdfText style={ticketPdfStyles.dateText}>
          {formatDateValue(ticket?.created_on || ticket?.time_out || ticket?.time_in)}
        </PdfText>
      </PdfView>

      <PdfView style={ticketPdfStyles.hero}>
        <PdfView style={ticketPdfStyles.heroRow}>
          <PdfView>
            <PdfText style={ticketPdfStyles.heroLabel}>Reference Number</PdfText>
            <PdfText style={ticketPdfStyles.heroValue}>
              {getDisplayValue(ticket?.weigh_bridge_ticket_reference_no || ticket?.weigh_bridge_ticket_number)}
            </PdfText>
            <PdfText style={ticketPdfStyles.heroSubValue}>
              Ticket #{getDisplayValue(ticket?.weigh_bridge_ticket_number)}
            </PdfText>
          </PdfView>
          <PdfView>
            <PdfText style={ticketPdfStyles.heroLabel}>Status</PdfText>
            <PdfText style={[ticketPdfStyles.heroValue, { fontSize: 12, color: getTicketToneHex(ticketStatusTone) }]}>
              {getDisplayValue(formatTicketStatus(ticket?.weigh_bridge_ticket_status))}
            </PdfText>
            <PdfText style={ticketPdfStyles.heroSubValue}>
              {getDisplayValue(formatDesignation(ticket?.designation))}
            </PdfText>
          </PdfView>
        </PdfView>
        <PdfView style={ticketPdfStyles.badgeRow}>
          <PdfView style={[ticketPdfStyles.badge, { backgroundColor: getTicketToneHex(ticketStatusTone) }]}>
            <PdfText style={ticketPdfStyles.badgeText}>{getDisplayValue(formatTicketStatus(ticket?.weigh_bridge_ticket_status))}</PdfText>
          </PdfView>
          <PdfView style={[ticketPdfStyles.badge, { backgroundColor: getTicketToneHex("info") }]}>
            <PdfText style={ticketPdfStyles.badgeText}>{getDisplayValue(ticket?.weigh_bridge_ticket_type)}</PdfText>
          </PdfView>
        </PdfView>
      </PdfView>

      <PdfView style={ticketPdfStyles.metricRow}>
        <PdfMetricCard label="Tare Weight" value={formatWeightValue(ticket?.first_weight)} tone="warning" />
        <PdfView style={ticketPdfStyles.metricGap} />
        <PdfMetricCard label="Gross Weight" value={formatWeightValue(ticket?.second_weight)} tone="primary" />
        <PdfView style={ticketPdfStyles.metricGap} />
        <PdfMetricCard label="Net Weight" value={formatWeightValue(ticket?.net_weight)} tone="success" />
      </PdfView>

      <PdfView style={ticketPdfStyles.sectionGrid}>
        <TicketPdfSection
          title="Ticket Overview"
          subtitle="Primary identifiers, workflow linkage, and sync metadata."
          fields={overviewFields}
        />
        <PdfView style={ticketPdfStyles.sectionGap} />
        <TicketPdfSection
          title="Vehicle Details"
          subtitle="Vehicle, transporter, and trailer information captured for this ticket."
          fields={vehicleFields}
        />
      </PdfView>

      <PdfView style={ticketPdfStyles.sectionGrid}>
        <TicketPdfSection
          title="Weight Timeline"
          subtitle="Weight values and timestamps recorded during the weighbridge process."
          fields={weightFields}
        />
        <PdfView style={ticketPdfStyles.sectionGap} />
        <TicketPdfSection
          title="Linked Document"
          subtitle="Related warehouse workflow records connected to this weighbridge ticket."
          fields={resolvedLinkedFields}
        />
      </PdfView>

      <PdfText style={ticketPdfStyles.poweredBy}>Powered by UAG</PdfText>
    </PdfPage>
  </PdfDocument>
  );
};

const renderTicketPrintFieldRows = (fields = []) =>
  fields
    .map(
      (field, index) => `
        <div class="field-row ${index === fields.length - 1 ? "last" : ""}">
          <div class="field-label">${escapeTicketHtml(field.label)}</div>
          <div class="field-value">${escapeTicketHtml(field.value)}</div>
        </div>
      `,
    )
    .join("");

const buildTicketPrintHtml = ({
  ticket = {},
  overviewFields = [],
  vehicleFields = [],
  weightFields = [],
  linkedDocumentFields = [],
}) => {
  const statusTone = getTicketStatusTone(ticket?.weigh_bridge_ticket_status);
  const statusColor = getTicketToneHex(statusTone);

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeTicketHtml(ticket?.weigh_bridge_ticket_reference_no || ticket?.weigh_bridge_ticket_number || "Weighbridge Ticket")}</title>
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            font-family: Arial, Helvetica, sans-serif;
            color: #0f172a;
            background: #f8fafc;
          }
          .page {
            padding: 24px;
          }
          .sheet {
            background: #ffffff;
            border: 1px solid #dbe4f0;
            border-radius: 18px;
            padding: 24px;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            margin-bottom: 16px;
          }
          .logo {
            width: 86px;
            height: auto;
          }
          .header-copy {
            flex: 1;
          }
          .header-copy h1 {
            font-size: 28px;
            margin: 0 0 4px;
          }
          .header-copy p, .header-date {
            margin: 0;
            color: #64748b;
            font-size: 12px;
          }
          .hero {
            border: 1px solid #dbe4f0;
            border-radius: 18px;
            padding: 18px;
            background: linear-gradient(135deg, #f7fbff 0%, #eef5ff 50%, #ffffff 100%);
            margin-bottom: 16px;
          }
          .hero-row {
            display: flex;
            justify-content: space-between;
            gap: 16px;
          }
          .eyebrow {
            font-size: 11px;
            text-transform: uppercase;
            color: #64748b;
            font-weight: 700;
            letter-spacing: 0.05em;
            margin-bottom: 6px;
          }
          .hero-value {
            font-size: 30px;
            font-weight: 700;
            margin: 0;
          }
          .hero-sub {
            color: #475569;
            margin-top: 6px;
            font-size: 14px;
          }
          .hero-status {
            text-align: right;
          }
          .hero-status .hero-value {
            font-size: 18px;
            color: ${statusColor};
          }
          .metrics {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 12px;
            margin-bottom: 16px;
          }
          .metric-card {
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
            padding: 16px;
            min-height: 116px;
          }
          .metric-label {
            font-size: 11px;
            text-transform: uppercase;
            color: #64748b;
            font-weight: 700;
            margin-bottom: 10px;
            line-height: 1.4;
          }
          .metric-value {
            font-size: 28px;
            font-weight: 700;
            line-height: 1.15;
            overflow-wrap: anywhere;
          }
          .metric-warning { color: #f59e0b; }
          .metric-primary { color: #2563eb; }
          .metric-success { color: #0f9d58; }
          .section-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
            margin-bottom: 12px;
          }
          .section-card {
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            background: #ffffff;
            padding: 16px;
          }
          .section-card h2 {
            margin: 0 0 4px;
            font-size: 18px;
          }
          .section-card p {
            margin: 0 0 8px;
            color: #64748b;
            font-size: 12px;
            line-height: 1.5;
          }
          .field-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
            padding: 10px 0;
            border-bottom: 1px solid #eef2f7;
          }
          .field-row.last {
            border-bottom: 0;
          }
          .field-label {
            width: 38%;
            color: #64748b;
            font-size: 11px;
            text-transform: uppercase;
            font-weight: 700;
            line-height: 1.45;
          }
          .field-value {
            width: 62%;
            text-align: right;
            font-size: 13px;
            font-weight: 600;
            line-height: 1.45;
            overflow-wrap: anywhere;
          }
          .field-value.success {
            color: #0f9d58;
          }
          .powered {
            margin-top: 10px;
            text-align: right;
            color: #64748b;
            font-size: 11px;
            font-weight: 700;
          }
          @media print {
            body {
              background: #ffffff;
            }
            .page {
              padding: 0;
            }
            .sheet {
              border: 0;
              border-radius: 0;
              padding: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="sheet">
            <div class="header">
              <img class="logo" src="${UAGLogo}" alt="UAG" />
              <div class="header-copy">
                <h1>Weighbridge Ticket</h1>
                <p>United African Grain</p>
              </div>
              <div class="header-date">${escapeTicketHtml(formatDateValue(ticket?.created_on || ticket?.time_out || ticket?.time_in))}</div>
            </div>

            <div class="hero">
              <div class="hero-row">
                <div>
                  <div class="eyebrow">Reference Number</div>
                  <div class="hero-value">${escapeTicketHtml(ticket?.weigh_bridge_ticket_reference_no || ticket?.weigh_bridge_ticket_number)}</div>
                  <div class="hero-sub">Ticket #${escapeTicketHtml(ticket?.weigh_bridge_ticket_number)}</div>
                </div>
                <div class="hero-status">
                  <div class="eyebrow">Status</div>
                  <div class="hero-value">${escapeTicketHtml(formatTicketStatus(ticket?.weigh_bridge_ticket_status))}</div>
                  <div class="hero-sub">${escapeTicketHtml(formatDesignation(ticket?.designation))}</div>
                </div>
              </div>
            </div>

            <div class="metrics">
              <div class="metric-card">
                <div class="metric-label">Tare Weight</div>
                <div class="metric-value metric-warning">${escapeTicketHtml(formatWeightValue(ticket?.first_weight))}</div>
              </div>
              <div class="metric-card">
                <div class="metric-label">Gross Weight</div>
                <div class="metric-value metric-primary">${escapeTicketHtml(formatWeightValue(ticket?.second_weight))}</div>
              </div>
              <div class="metric-card">
                <div class="metric-label">Net Weight</div>
                <div class="metric-value metric-success">${escapeTicketHtml(formatWeightValue(ticket?.net_weight))}</div>
              </div>
            </div>

            <div class="section-grid">
              <div class="section-card">
                <h2>Ticket Overview</h2>
                <p>Primary identifiers, workflow linkage, and sync metadata.</p>
                ${renderTicketPrintFieldRows(overviewFields)}
              </div>
              <div class="section-card">
                <h2>Vehicle Details</h2>
                <p>Vehicle, transporter, and trailer information captured for this ticket.</p>
                ${renderTicketPrintFieldRows(vehicleFields)}
              </div>
            </div>

            <div class="section-grid">
              <div class="section-card">
                <h2>Weight Timeline</h2>
                <p>Weight values and timestamps recorded during the weighbridge process.</p>
                ${renderTicketPrintFieldRows(weightFields)}
              </div>
              <div class="section-card">
                <h2>Linked Document</h2>
                <p>Related warehouse workflow records connected to this weighbridge ticket.</p>
                ${renderTicketPrintFieldRows(linkedDocumentFields)}
              </div>
            </div>

            <div class="powered">Powered by UAG</div>
          </div>
        </div>
      </body>
    </html>
  `;
};

const normalizeWeighbridgeTickets = (rows = []) => {
  return rows.map((item, index) => {
    const { ticket, ticketStatus, ticketType, backendTicketId } =
      resolveWeighbridgeTicketRecord(item);

    const directTicketNumber = pickFirstPresent(
      item?.weigh_bridge_ticket_number,
      ticket?.weigh_bridge_ticket_number,
      item?.ticket_number,
      item?.weighbridge_ticket_number,
    );
    const directReferenceNumber = pickFirstPresent(
      item?.weigh_bridge_ticket_reference_no,
      ticket?.weigh_bridge_ticket_reference_no,
      item?.reference_no,
      item?.weighbridge_ticket_reference_no,
    );
    const directTruckPlate = pickFirstPresent(
      item?.truck_licence_plate,
      ticket?.truck_licence_plate,
      item?.truck_license_plate,
      ticket?.truck_license_plate,
      item?.vehicle_number,
    );
    const directStatus = pickFirstPresent(
      item?.weigh_bridge_ticket_status,
      ticketStatus?.weigh_bridge_ticket_status,
      ticket?.weigh_bridge_ticket_status,
    );
    const directUsageStatus = pickFirstPresent(item?.usage_status, ticket?.usage_status);
    const directFirstWeight = pickFirstPresent(item?.first_weight, ticket?.first_weight);
    const directSecondWeight = pickFirstPresent(item?.second_weight, ticket?.second_weight);
    const directNetWeight = pickFirstPresent(item?.net_weight, ticket?.net_weight);

    const shouldUseDeepPrefill = !(
      directTicketNumber &&
      directReferenceNumber &&
      directTruckPlate &&
      directStatus &&
      directFirstWeight &&
      directSecondWeight &&
      directNetWeight
    );

    const prefillData = shouldUseDeepPrefill ? resolveWeighbridgeTicketPrefillData(item) : {};

    const weighBridgeTicketNumber = directTicketNumber || prefillData?.weigh_bridge_ticket_number || "";
    const weighBridgeTicketReferenceNo =
      directReferenceNumber || prefillData?.weigh_bridge_ticket_reference_no || "";
    const externalWeighBridgeTicketNumber = pickFirstPresent(
      item?.external_weigh_bridge_ticket_number,
      ticket?.external_weigh_bridge_ticket_number,
      prefillData?.external_weigh_bridge_ticket_number,
    );
    const firstWeight = pickFirstPresent(directFirstWeight, prefillData?.first_weight);
    const secondWeight = pickFirstPresent(directSecondWeight, prefillData?.second_weight);
    const netWeight = pickFirstPresent(directNetWeight, prefillData?.net_weight);
    const timeIn = pickFirstPresent(item?.time_in, ticket?.time_in, prefillData?.time_in);
    const timeOut = pickFirstPresent(item?.time_out, ticket?.time_out, prefillData?.time_out);
    const createdOn = pickFirstPresent(item?.created_on, ticket?.created_on, prefillData?.created_on, timeIn);
    const truckDriverName = pickFirstPresent(
      item?.truck_driver_name,
      ticket?.truck_driver_name,
      prefillData?.truck_driver_name,
    );
    const transporterName = pickFirstPresent(
      item?.transporter_name,
      ticket?.transporter_name,
      prefillData?.transporter_name,
    );
    const truckLicencePlate = directTruckPlate || prefillData?.truck_licence_plate || "";
    const designation = pickFirstPresent(item?.designation, ticket?.designation, prefillData?.designation);
    const branchLocationId = pickFirstPresent(
      item?.branch_location_id,
      ticket?.branch_location_id,
      prefillData?.branch_location_id,
    );
    const sourceSystem = pickFirstPresent(item?.source_system, ticket?.source_system, prefillData?.source_system);
    const syncBatchId = pickFirstPresent(item?.sync_batch_id, ticket?.sync_batch_id, prefillData?.sync_batch_id);
    const trailerOne = pickFirstPresent(item?.trailer_one, ticket?.trailer_one, prefillData?.trailer_one);
    const trailerTwo = pickFirstPresent(item?.trailer_two, ticket?.trailer_two, prefillData?.trailer_two);
    const comments = pickFirstPresent(item?.comments, ticket?.comments, prefillData?.comments);
    const rawPayload = item?.raw_payload || ticket?.raw_payload || prefillData?.raw_payload || null;
    const usageStatus =
      directUsageStatus ||
      (item?.goods_received_id || item?.goods_received_ref_number || item?.delivery_note_id || item?.delivery_note_ref_number
        ? "USED"
        : "") ||
      prefillData?.usage_status ||
      resolveWeighbridgeUsageStatus(item);
    const normalizedStatus = directStatus || prefillData?.weigh_bridge_ticket_status || "";
    const normalizedType = pickFirstPresent(
      item?.weigh_bridge_ticket_type,
      ticketType?.weigh_bride_ticket_type,
      ticketType?.weigh_bridge_ticket_type,
      ticket?.weigh_bridge_ticket_type,
      prefillData?.weigh_bridge_ticket_type,
    );
    const searchBlob = [
      weighBridgeTicketNumber,
      weighBridgeTicketReferenceNo,
      externalWeighBridgeTicketNumber,
      truckLicencePlate,
      truckDriverName,
      transporterName,
      designation,
      normalizedStatus,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return {
      id:
        backendTicketId ||
        prefillData?.weighbridge_ticket_id ||
        weighBridgeTicketNumber ||
        weighBridgeTicketReferenceNo ||
        index,
      weighbridge_ticket_id: backendTicketId || prefillData?.weighbridge_ticket_id || "",
      raw: item,
      weigh_bridge_ticket_number: weighBridgeTicketNumber,
      weigh_bridge_ticket_reference_no: weighBridgeTicketReferenceNo,
      external_weigh_bridge_ticket_number: externalWeighBridgeTicketNumber,
      first_weight: firstWeight,
      second_weight: secondWeight,
      net_weight: netWeight,
      time_in: timeIn,
      time_out: timeOut,
      created_on: createdOn,
      truck_driver_name: truckDriverName,
      transporter_name: transporterName,
      truck_licence_plate: truckLicencePlate,
      designation,
      branch_location_id: branchLocationId,
      source_system: sourceSystem,
      sync_batch_id: syncBatchId,
      trailer_one: trailerOne,
      trailer_two: trailerTwo,
      comments,
      raw_payload: rawPayload,
      usage_status: usageStatus,
      weigh_bridge_ticket_status_id:
        prefillData?.weigh_bridge_ticket_status_id ||
        ticket?.weigh_bridge_ticket_status_id ||
        item?.weigh_bridge_ticket_status_id ||
        ticketStatus?.weigh_bridge_ticket_status_id ||
        "",
      weigh_bridge_ticket_status: normalizedStatus,
      weigh_bridge_ticket_type_id:
        prefillData?.weigh_bridge_ticket_type_id ||
        ticket?.weigh_bridge_ticket_type_id ||
        item?.weigh_bridge_ticket_type_id ||
        ticketType?.weigh_bridge_ticket_type_id ||
        "",
      weigh_bridge_ticket_type: normalizedType,
      search_blob: searchBlob,
    };
  });
};

const extractTicketList = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.weigh_bridge_tickets)) return responseData.weigh_bridge_tickets;
  if (Array.isArray(responseData?.tickets)) return responseData.tickets;
  if (Array.isArray(responseData?.data)) return responseData.data;
  return [];
};

const extractTicketDetails = (responseData) => {
  const payload = responseData?.data || responseData || {};
  return payload?.Weighbridge_ticket || payload?.weighbridge_ticket || payload?.weigh_bridge_ticket || payload;
};

const getTicketReferenceNo = (ticket = {}) =>
  ticket?.weigh_bridge_ticket_reference_no ||
  ticket?.ticket?.weigh_bridge_ticket_reference_no ||
  ticket?.raw?.ticket?.weigh_bridge_ticket_reference_no ||
  "";

const getTicketExternalNo = (ticket = {}) =>
  ticket?.external_weigh_bridge_ticket_number ||
  ticket?.ticket?.external_weigh_bridge_ticket_number ||
  ticket?.raw?.ticket?.external_weigh_bridge_ticket_number ||
  "";

const getTicketInternalId = (ticket = {}) =>
  ticket?.weighbridge_ticket_id ||
  ticket?.weigh_bridge_ticket_id ||
  ticket?.ticket?.weighbridge_ticket_id ||
  ticket?.ticket?.weigh_bridge_ticket_id ||
  ticket?.raw?.ticket?.weighbridge_ticket_id ||
  ticket?.raw?.ticket?.weigh_bridge_ticket_id ||
  ticket?.id ||
  "";

const getDetailGoodsReceived = (details = {}) => {
  const goodsReceived = details?.goods_received || null;
  return goodsReceived?.goods_received || goodsReceived;
};

const getDetailDeliveryNote = (details = {}) => {
  const deliveryNote = details?.delivery_note || null;
  return deliveryNote?.delivery_note || deliveryNote;
};

const getLinkedDocuments = (item = {}) => {
  const linkedDocuments = [];
  const goodsReceivedRefNumber = pickWeighbridgeValue(item, ["goods_received_ref_number"], "");
  const goodsReceivedId = pickWeighbridgeValue(item, ["goods_received_id"], "");
  const deliveryNoteRefNumber = pickWeighbridgeValue(item, ["delivery_note_ref_number"], "");
  const deliveryNoteId = pickWeighbridgeValue(item, ["delivery_note_id"], "");
  const loadingOrderRefNumber = pickWeighbridgeValue(item, ["loading_order_ref_no"], "");
  const loadingOrderId = pickWeighbridgeValue(item, ["loading_order_id"], "");

  if (goodsReceivedRefNumber || goodsReceivedId) {
    linkedDocuments.push({
      key: `grn:${goodsReceivedRefNumber || goodsReceivedId}`,
      label: "GRN",
      reference: goodsReceivedRefNumber || goodsReceivedId,
      tone: "primary",
    });
  }

  if (deliveryNoteRefNumber || deliveryNoteId) {
    linkedDocuments.push({
      key: `dn:${deliveryNoteRefNumber || deliveryNoteId}`,
      label: "DN",
      reference: deliveryNoteRefNumber || deliveryNoteId,
      tone: "success",
    });
  }

  if (loadingOrderRefNumber || loadingOrderId) {
    linkedDocuments.push({
      key: `lo:${loadingOrderRefNumber || loadingOrderId}`,
      label: "Loading Order",
      reference: loadingOrderRefNumber || loadingOrderId,
      tone: "warning",
    });
  }

  return linkedDocuments;
};

const formatLinkedDocumentSummary = (documents = []) => {
  if (!documents.length) {
    return "Available";
  }

  return documents
    .map((document) =>
      document?.reference ? `${document.label} (${document.reference})` : document.label,
    )
    .join(", ");
};

const shouldIgnoreTicketRowClick = (target) => {
  if (!target || typeof target.closest !== "function") return false;

  return Boolean(
    target.closest(
      "a, button, input, select, textarea, label, .nk-tb-col-tools, .dropdown-toggle, .dropdown-menu, .nav-link",
    ),
  );
};

const getApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message ||
  error?.response?.data?.errors?.detail ||
  error?.message ||
  fallbackMessage;

const TicketSelectorPage = () => {
  // ======================================================
  // STATE
  // ======================================================
  const dispatch = useDispatch();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [syncStatus, setSyncStatus] = useState("");
  const [syncTone, setSyncTone] = useState("info");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [ticketDetailsLoading, setTicketDetailsLoading] = useState(false);
  const [ticketDetailsError, setTicketDetailsError] = useState("");
  const [agentLookupStatus, setAgentLookupStatus] = useState("");
  const [agentLookupTone, setAgentLookupTone] = useState("info");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = usePersistentState("weighbridge_ticket_selector:searchTerm", "");
  const [selectedFilter, setSelectedFilter] = usePersistentState(
    "weighbridge_ticket_selector:selectedFilter",
    "All Statuses"
  );
  const [selectedDateFilter, setSelectedDateFilter] = usePersistentState(
    "weighbridge_ticket_selector:selectedDateFilter",
    "All Dates"
  );

  const [sm, updateSm] = useState(false);
  const navigate = useNavigate();

  // pagination
  const [currentPage, setCurrentPage] = usePersistentState("weighbridge_ticket_selector:currentPage", 1);
  const [itemPerPage] = useState(10);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const skipInitialPageResetRef = useRef(true);

  const handleUnauthorizedError = useCallback(
    (apiError) => {
      if (apiError?.response?.status !== 401) return false;

      dispatch(success_sign_out_modal());
      return true;
    },
    [dispatch]
  );

  const fetchTickets = useCallback(async ({ runAgentSync = false } = {}) => {
    try
    {
      setError("");
      setLoading(true);

      if (runAgentSync)
      {
        setSyncTone("info");
        setSyncStatus("Syncing latest weighbridge tickets from the local agent...");

        try
        {
          const syncResult = await syncWeighbridgeTicketsFromAgent({
            onStatus: (status) => setSyncStatus(status),
          });

          setSyncTone("success");
          setSyncStatus(syncResult?.message || "Weighbridge tickets synced successfully.");
        } catch (syncError)
        {
          if (handleUnauthorizedError(syncError))
          {
            throw syncError;
          }

          setSyncTone("warning");
          setSyncStatus("");
        }
      } else
      {
        setSyncStatus("");
      }

      const response = await makeEmployeeDashboardApiRequest("get", RETRIEVE_ALL_WEIGH_BRIDGE_TICKETS_API);
      const responseData = response?.data?.data || response?.data || [];
      const rows = extractTicketList(responseData);
        const normalized = sortWeighbridgeTicketsDesc(normalizeWeighbridgeTickets(rows));

      setTickets(normalized);
    } catch (fetchError)
    {
      if (handleUnauthorizedError(fetchError))
      {
        setError("Your session has expired. Please sign in again to continue.");
      } else
      {
        setError(getApiErrorMessage(fetchError, "Failed to retrieve weighbridge tickets."));
      }

      setTickets([]);
    } finally
    {
      setLoading(false);
    }
  }, [handleUnauthorizedError]);

  useEffect(() => {
    fetchTickets({ runAgentSync: false });
  }, [fetchTickets]);

  // ======================================================
  // PAGINATION DATA
  // ======================================================
  const statusFilters = useMemo(() => {
    const uniqueStatuses = Array.from(
      new Set(
        tickets
          .map((ticket) => ticket.weigh_bridge_ticket_status)
          .filter((value) => value && value !== "--")
      )
    );

    return ["All Statuses", ...uniqueStatuses];
  }, [tickets]);

  const current_page_data = useMemo(() => {
    let filteredTickets = tickets;

    if (selectedFilter !== "All Statuses")
    {
      filteredTickets = filteredTickets.filter((ticket) => ticket.weigh_bridge_ticket_status === selectedFilter);
    }

    if (selectedDateFilter !== "All Dates")
    {
      filteredTickets = filteredTickets.filter((ticket) => matchesWeighbridgeTicketDateFilter(ticket, selectedDateFilter));
    }

    if (searchTerm.trim())
    {
      const lowerSearch = searchTerm.toLowerCase();
      filteredTickets = filteredTickets.filter((ticket) => ticket.search_blob?.includes(lowerSearch));
    }

    return filteredTickets;
  }, [tickets, searchTerm, selectedDateFilter, selectedFilter]);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = current_page_data?.slice(indexOfFirstItem, indexOfLastItem);
  const hasActiveFilters =
    searchTerm.trim() !== "" ||
    selectedFilter !== "All Statuses" ||
    selectedDateFilter !== "All Dates";

  useEffect(() => {
    if (skipInitialPageResetRef.current) {
      skipInitialPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [searchTerm, selectedDateFilter, selectedFilter, setCurrentPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(current_page_data.length / itemPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, current_page_data.length, itemPerPage, setCurrentPage]);

  const refreshList = () => {
    setTickets([]);
    fetchTickets({ runAgentSync: false });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedFilter("All Statuses");
    setSelectedDateFilter("All Dates");
  };

  const resyncAndRefreshList = () => {
    setTickets([]);
    fetchTickets({ runAgentSync: true });
  };

  const retrieveTicketDetails = useCallback(async (ticket) => {
    const internalId = getTicketInternalId(ticket);
    const referenceNo = getTicketReferenceNo(ticket);

    if (internalId) {
      try {
        const response = await WeighbridgeService.getTicket(internalId);
        return extractTicketDetails(response?.data);
      } catch (internalIdError) {
        if (!referenceNo) {
          throw internalIdError;
        }
      }
    }

    if (!referenceNo) {
      throw new Error("This ticket does not have an internal id or reference number for detail retrieval.");
    }

    const response = await makeEmployeeDashboardApiRequest(
      "get",
      RETRIEVE_WEIGH_BRIDGE_TICKET_DETAILS_API(referenceNo)
    );

    return extractTicketDetails(response?.data);
  }, []);

  const loadTicketDetails = useCallback(
    async (ticket, { runAgentLookup = false } = {}) => {
      if (!ticket) return;

      try
      {
        setTicketDetailsLoading(true);
        setTicketDetailsError("");
        setAgentLookupStatus("");
        setAgentLookupTone("info");

        let details = await retrieveTicketDetails(ticket);
        setTicketDetails(details);

        if (runAgentLookup)
        {
          const detailTicket = details?.ticket || ticket;
          const externalTicketNumber = getTicketExternalNo(detailTicket) || getTicketExternalNo(ticket);

          if (!externalTicketNumber)
          {
            throw new Error("This ticket does not have an external ticket number for agent lookup.");
          }

          const syncResult = await updateWeighbridgeTicketFromAgent({
            ticket: detailTicket,
            onStatus: (status) => setAgentLookupStatus(status),
          });

          setAgentLookupTone("success");
          setAgentLookupStatus(syncResult?.message || "Weighbridge ticket resynced successfully.");

          details = await retrieveTicketDetails(ticket);
          setTicketDetails(details);
          fetchTickets({ runAgentSync: false });
        }
      } catch (detailsError)
      {
        if (handleUnauthorizedError(detailsError))
        {
          setTicketDetailsError("Your session has expired. Please sign in again to continue.");
        } else
        {
          setAgentLookupTone("warning");
          setTicketDetailsError(getApiErrorMessage(detailsError, "Failed to retrieve ticket details."));
        }
      } finally
      {
        setTicketDetailsLoading(false);
      }
    },
    [fetchTickets, handleUnauthorizedError, retrieveTicketDetails]
  );

  const openDetailsModal = (ticket, { runAgentLookup = false } = {}) => {
    setSelectedTicket(ticket);
    setTicketDetails(null);
    setTicketDetailsError("");
    setAgentLookupStatus("");
    setShowDetailsModal(true);
    loadTicketDetails(ticket, { runAgentLookup });
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
  };

  const printTicketDetails = () => {
    const linkedPrintFields = hasLinkedDocument
      ? linkedDocumentFields
      : [
          { label: "Consigner Type", value: activeConsignerType?.consigner_type || "---" },
          { label: "Comments", value: activeDetailTicket?.comments || "---" },
        ];

    const printWindow = window.open("", "_blank", "width=1100,height=900");

    if (!printWindow) {
      return;
    }

    printWindow.document.write(
      buildTicketPrintHtml({
        ticket: activeDetailTicket,
        overviewFields,
        vehicleFields,
        weightFields,
        linkedDocumentFields: linkedPrintFields,
      }),
    );
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const downloadTicketDetails = () => {
    const normalizedDetailTicket = resolveWeighbridgeTicketPrefillData(ticketDetails || {});
    const ticketForPdf = {
      ...(selectedTicket || {}),
      ...normalizedDetailTicket,
      ...((ticketDetails || {})?.ticket || {}),
    };
    if (!ticketForPdf) return;
    const createPdf = async () => {
      const ticketNumber =
        ticketForPdf?.weigh_bridge_ticket_reference_no ||
        ticketForPdf?.weigh_bridge_ticket_number ||
        "weighbridge-ticket";

      const fileName = `${String(ticketNumber).replace(/[^\w-]+/g, "_")}.pdf`;
      const blob = await pdf(
        <TicketPdfDocument
          ticket={ticketForPdf}
          linkedDocumentFields={
            hasLinkedDocument
              ? linkedDocumentFields
              : [{ label: "Comments", value: ticketForPdf?.comments || "---" }]
          }
          consignerType={activeConsignerType?.consigner_type || "---"}
        />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    createPdf();
  };

  const formatDate = (iso) => {
    return formatDateValue(iso);
  };

  const startGoodsReceivedFlow = useCallback(
    (ticket) => {
      if (!canStartGoodsReceivedWorkflowFromTicket(ticket)) {
        return;
      }

      navigate("/dashboard/goods_received/create_physical_goods_received_ticket", {
        state: { selectedWeighbridgeTicket: ticket },
      });
    },
    [navigate]
  );

  const continueGoodsReceivedFlow = useCallback(
    (goodsReceivedRefNumber) => {
      if (!goodsReceivedRefNumber) {
        return;
      }

      navigate(`/dashboard/goods_received/continue_processing_physical/${goodsReceivedRefNumber}`);
    },
    [navigate]
  );

  const startDeliveryNoteFlow = useCallback(
    (ticket) => {
      navigate("/dashboard/delivery_notes/create_physical_delivery_note_ticket", {
        state: { selectedWeighbridgeTicket: ticket },
      });
    },
    [navigate]
  );

  const activeTicketDetails = ticketDetails || {};
  const activeDetailPrefill = resolveWeighbridgeTicketPrefillData(activeTicketDetails);
  const activeDetailTicket = {
    ...(selectedTicket || {}),
    ...activeDetailPrefill,
    ...(activeTicketDetails?.ticket || {}),
  };
  const activeTicketType = activeTicketDetails?.ticket_type || {};
  const activeTicketStatus = activeTicketDetails?.ticket_status || {};
  const activeGoodsReceived = getDetailGoodsReceived(activeTicketDetails);
  const activeDeliveryNote = getDetailDeliveryNote(activeTicketDetails);
  const activeLinkedDocuments = getLinkedDocuments(activeTicketDetails);
  const activeConsignerType =
    activeTicketDetails?.goods_received?.consigner_type ||
    activeTicketDetails?.consigner_type ||
    activeTicketDetails?.delivery_note?.consigner_type ||
    {};
  const activeTicketStatusText =
    activeTicketStatus?.weigh_bridge_ticket_status ||
    activeDetailPrefill?.weigh_bridge_ticket_status ||
    activeDetailTicket?.weigh_bridge_ticket_status;
  const activeTicketTypeText =
    activeTicketType?.weigh_bride_ticket_type ||
    activeTicketType?.weigh_bridge_ticket_type ||
    activeDetailPrefill?.weigh_bridge_ticket_type ||
    activeDetailTicket?.weigh_bridge_ticket_type;
  const activeTicketReferenceNo = getTicketReferenceNo(activeDetailTicket);
  const activeExternalTicketNo = getTicketExternalNo(activeDetailTicket);
  const canUpdateExternalTicket = Boolean(activeExternalTicketNo);
  const hasLinkedDocument = Boolean(
    activeGoodsReceived?.goods_received_ref_number || activeDeliveryNote?.delivery_note_ref_number
  );
  const overviewFields = [
    { label: "Reference Number", value: activeTicketReferenceNo || "---" },
    { label: "Ticket Number", value: activeDetailTicket?.weigh_bridge_ticket_number || "---" },
    { label: "External Reference", value: activeExternalTicketNo || "---" },
    { label: "Transaction Type", value: activeTicketTypeText || "---" },
    { label: "Created On", value: formatDate(activeDetailTicket?.created_on || activeDetailTicket?.time_in) },
    { label: "Linked To", value: formatLinkedDocumentSummary(activeLinkedDocuments) },
    { label: "Source System", value: activeDetailTicket?.source_system || "---" },
    { label: "Sync Batch", value: activeDetailTicket?.sync_batch_id || "---" },
  ];
  const vehicleFields = [
    { label: "Truck Reg No", value: activeDetailTicket?.truck_licence_plate || "---" },
    { label: "Truck Driver", value: activeDetailTicket?.truck_driver_name || "---" },
    { label: "Transporter", value: activeDetailTicket?.transporter_name || "---" },
    { label: "Trailer One", value: activeDetailTicket?.trailer_one || "---" },
    { label: "Trailer Two", value: activeDetailTicket?.trailer_two || "---" },
  ];
  const weightFields = [
    { label: "Tare Weight", value: formatWeightValue(activeDetailTicket?.first_weight) },
    { label: "Gross Weight", value: formatWeightValue(activeDetailTicket?.second_weight) },
    { label: "Net Weight", value: formatWeightValue(activeDetailTicket?.net_weight), valueClassName: "text-success" },
    { label: "Time In", value: formatDate(activeDetailTicket?.time_in) },
    { label: "Time Out", value: formatDate(activeDetailTicket?.time_out) },
  ];
  const linkedDocumentFields = [
    ...(activeGoodsReceived?.goods_received_ref_number
      ? [
          { label: "GRN Reference", value: activeGoodsReceived.goods_received_ref_number || "---" },
          { label: "GRN Origin", value: formatDesignation(activeGoodsReceived?.grn_origin) },
          { label: "Contract Number", value: activeGoodsReceived?.contract_number || "---" },
        ]
      : []),
    ...(activeDeliveryNote?.delivery_note_ref_number
      ? [
          { label: "DN Reference", value: activeDeliveryNote.delivery_note_ref_number || "---" },
          { label: "DN Origin", value: formatDesignation(activeDeliveryNote?.dn_origin) },
          { label: "Delivery Location", value: activeDeliveryNote?.delivery_location || "---" },
        ]
      : []),
    { label: "Consigner Type", value: activeConsignerType?.consigner_type || "---" },
  ];
  const handleUpdateTicketClick = () => {
    if (!canUpdateExternalTicket) {
      messageToast(
        "This ticket cannot be updated from the external weighbridge system because it has no external ticket number.",
        app_consts.FAIL,
      );
      return;
    }

    loadTicketDetails(selectedTicket || activeDetailTicket, { runAgentLookup: true });
  };

  return (
    <>
      <Head title="Weighbridge Tickets" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Weighbridge Tickets</BlockTitle>
              <BlockDes className="text-soft">
                <p>{!(loading || error) && ` Weighbridge Tickets ${current_page_data?.length}`}</p>
              </BlockDes>
            </BlockHeadContent>

            {!(loading || error) && (
              <BlockHeadContent>
                <div className="toggle-wrap nk-block-tools-toggle">
                  <a
                    href="#more"
                    className="btn btn-icon btn-trigger toggle-expand me-n1"
                    onClick={(ev) => {
                      ev.preventDefault();
                      updateSm(!sm);
                    }}
                  >
                    <Icon name="more-v"></Icon>
                  </a>

                  <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                    <ul className="nk-block-tools g-3">
                      <li>
                        <UagButton color="light" outline className="btn-white" onClick={refreshList}>
                          <Icon name="repeat"></Icon>
                          <span>Refresh</span>
                        </UagButton>
                      </li>
                      <li>
                        <UagButton color="primary" outline className="btn-white" onClick={resyncAndRefreshList}>
                          <Icon name="reload"></Icon>
                          <span>Resync</span>
                        </UagButton>
                      </li>
                    </ul>
                  </div>
                </div>
              </BlockHeadContent>
            )}
          </BlockBetween>
        </BlockHead>

        {syncStatus && !loading && (
          <div className="mb-3">
            <Alert color={syncTone} className="mb-0">
              {syncStatus}
            </Alert>
          </div>
        )}

        {loading ? (
          <AuthLoader loading_message="Loading Weighbridge Tickets..." />
        ) : error ? (
          <div className="p-5 d-flex justify-content-center">
            <span className="text-danger fw-bold">{error}</span>
          </div>
        ) : tickets.length > 0 ? (
          <>
            <ul className="nk-block-tools g-3 d-flex justify-end mb-2">
              <li>
                <div>
                  <div className="form-icon form-icon-right">
                    <Icon name="search"></Icon>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search for Tickets"
                  />
                </div>
              </li>
              <li className="preview-item">
                <UncontrolledDropdown>
                  <div className="btn-group">
                    <DropdownToggle color="light" outline className="dropdown-toggle-split btn-white">
                      <Icon name="chevron-down"></Icon>
                      <span>{selectedFilter}</span>
                    </DropdownToggle>
                  </div>
                  <DropdownMenu className="mt-3">
                    <ul className="link-list-opt">
                      {statusFilters.map((status) => (
                        <li key={status}>
                          <DropdownItem
                            tag="a"
                            href="#filter"
                            onClick={(ev) => {
                              ev.preventDefault();
                              setSelectedFilter(status);
                            }}
                          >
                            <span>{status}</span>
                          </DropdownItem>
                        </li>
                      ))}
                    </ul>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </li>
              <li className="preview-item">
                <UncontrolledDropdown>
                  <div className="btn-group">
                    <DropdownToggle color="light" outline className="dropdown-toggle-split btn-white">
                      <Icon name="calendar"></Icon>
                      <span>{selectedDateFilter}</span>
                    </DropdownToggle>
                  </div>
                  <DropdownMenu className="mt-3">
                    <ul className="link-list-opt">
                      {WEIGHBRIDGE_DATE_FILTERS.map((dateFilter) => (
                        <li key={dateFilter}>
                          <DropdownItem
                            tag="a"
                            href="#date-filter"
                            onClick={(ev) => {
                              ev.preventDefault();
                              setSelectedDateFilter(dateFilter);
                            }}
                          >
                            <span>{dateFilter}</span>
                          </DropdownItem>
                        </li>
                      ))}
                    </ul>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </li>
              <li>
                <Button
                  type="button"
                  color="light"
                  outline
                  className="btn-white"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear Filters
                </Button>
              </li>
            </ul>

            <div className="nk-tb-list is-separate is-medium mt-1 mb-3">
              <div className="card-inner p-0">
                <DataTableBody>
                  <DataTableHead>
                    <DataTableRow size="sm">
                      <span>Ticket Ref No.</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>External Ref No.</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Truck Plate</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Driver</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Transporter</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Designation</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Ticket Status</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Created On</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Action</span>
                    </DataTableRow>
                  </DataTableHead>

                  {currentItems?.length > 0
                    ? currentItems.map((t) => (
                      <DataTableItem
                        key={t.id}
                        className="cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          if (shouldIgnoreTicketRowClick(event.target)) return;
                          openDetailsModal(t);
                        }}
                        onKeyDown={(event) => {
                          if (shouldIgnoreTicketRowClick(event.target)) return;
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          openDetailsModal(t);
                        }}
                      >
                        <DataTableRow size="sm">
                          <span className="tb-product">
                            <span className="title">{t.weigh_bridge_ticket_reference_no || t.weigh_bridge_ticket_number || "--"}</span>
                          </span>
                          <div>
                            <span className="fw-medium">Ticket No: </span>
                            <span className="tb-sub">{t.weigh_bridge_ticket_number || "--"}</span>
                          </div>
                        </DataTableRow>

                        <DataTableRow>
                          <span className="tb-sub">{t.external_weigh_bridge_ticket_number || "--"}</span>
                        </DataTableRow>

                        <DataTableRow>
                          <span className="tb-sub">{t.truck_licence_plate || "--"}</span>
                        </DataTableRow>

                        <DataTableRow>
                          <span className="tb-sub">{t.truck_driver_name || "--"}</span>
                        </DataTableRow>

                        <DataTableRow>
                          <span className="tb-sub">{t.transporter_name || "--"}</span>
                        </DataTableRow>

                        <DataTableRow>
                          <span className="tb-sub">{formatDesignation(t.designation)}</span>
                        </DataTableRow>

                        <DataTableRow size="md">
                          <span className="tb-odr-status">
                            <Badge color={getTicketStatusTone(t.weigh_bridge_ticket_status)} className="badge-dot">
                              {formatTicketStatus(t.weigh_bridge_ticket_status)}
                            </Badge>
                          </span>
                        </DataTableRow>

                        <DataTableRow size="md">
                          <span className="tb-sub">{formatDate(t.created_on || t.time_in)}</span>
                        </DataTableRow>

                        <DataTableRow className="nk-tb-col-tools">
                          <div className="tb-odr-btns d-none d-sm-inline">
                            <UncontrolledDropdown>
                              <DropdownToggle color="primary" size="sm" className="btn btn-dim dropdown-toggle">
                                Action
                              </DropdownToggle>
                              <DropdownMenu end>
                                <DropdownItem
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openDetailsModal(t);
                                  }}
                                >
                                  View Details
                                </DropdownItem>
                                <DropdownItem
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openDetailsModal(t, { runAgentLookup: true });
                                  }}
                                  disabled={!getTicketExternalNo(t)}
                                >
                                  Update Ticket
                                </DropdownItem>
                                {canStartGoodsReceivedWorkflowFromTicket(t) && (
                                    <DropdownItem
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        startGoodsReceivedFlow(t);
                                      }}
                                    >
                                      Start GRN Process
                                    </DropdownItem>
                                  )}
                                {canStartWorkflowFromTicket(t, "NOT SET") && (
                                    <DropdownItem
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        startDeliveryNoteFlow(t);
                                      }}
                                    >
                                      Start Delivery Note Process
                                    </DropdownItem>
                                  )}
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </div>

                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            className="btn-pd-auto d-sm-none"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openDetailsModal(t);
                            }}
                          >
                            <Icon name="eye" />
                          </Button>
                        </DataTableRow>
                      </DataTableItem>
                    ))
                    : null}
                </DataTableBody>

                {currentItems?.length === 0 && (
                  <div className="p-4 text-center text-soft fw-bold border-top">
                    No matching weighbridge tickets found.
                  </div>
                )}

                {currentItems?.length > 0 && (
                  <div className="mt-3">
                    <PaginationComponent
                      itemPerPage={itemPerPage}
                      totalItems={current_page_data?.length}
                      paginate={paginate}
                      currentPage={currentPage}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="p-5 text-center text-soft fw-bold">
            No Weighbridge Tickets Found.
          </div>
        )}

        <Modal isOpen={showDetailsModal} toggle={closeDetailsModal} size="xl" className="modal-dialog-centered">
          <ModalHeader toggle={closeDetailsModal} className="border-0 pb-0">
            Ticket Details
            <small className="text-primary d-block mt-1">
              #{activeTicketReferenceNo || activeDetailTicket?.weigh_bridge_ticket_number || "--"}
            </small>
          </ModalHeader>
          <ModalBody>
            {agentLookupStatus && (
              <Alert color={agentLookupTone} className="mb-3">
                {agentLookupStatus}
              </Alert>
            )}

            {ticketDetailsError && (
              <Alert color="danger" className="mb-3">
                {ticketDetailsError}
              </Alert>
            )}

            <div className="d-flex justify-content-end flex-wrap gap-2 mb-3 no-print">
              {activeGoodsReceived?.goods_received_ref_number && (
                <Button
                  color="primary"
                  className="btn-dim"
                  onClick={() => continueGoodsReceivedFlow(activeGoodsReceived.goods_received_ref_number)}
                >
                  <Icon name="clipboard" className="me-1" />
                  Continue Processing GRN
                </Button>
              )}
              {activeGoodsReceived?.goods_received_ref_number && (
                <Button
                  color="success"
                  className="btn-dim"
                  onClick={() => navigate(`/dashboard/goods_received/${activeGoodsReceived.goods_received_ref_number}`)}
                >
                  <Icon name="external" className="me-1" />
                  View GRN
                </Button>
              )}
              {activeDeliveryNote?.delivery_note_ref_number && (
                <Button
                  color="success"
                  className="btn-dim"
                  onClick={() => navigate(`/dashboard/delivery_notes/${activeDeliveryNote.delivery_note_ref_number}`)}
                >
                  <Icon name="external" className="me-1" />
                  View DN
                </Button>
              )}
              <Button color="light" className="btn-dim me-2" onClick={downloadTicketDetails}>
                <Icon name="download" className="me-1" />
                Download
              </Button>
              <Button
                color="warning"
                className="btn-dim"
                onClick={handleUpdateTicketClick}
                disabled={ticketDetailsLoading}
              >
                <Icon name="reload" className="me-1" />
                Update Ticket
              </Button>
              <Button color="primary" className="btn-dim" onClick={printTicketDetails}>
                <Icon name="printer" className="me-1" />
                Print Ticket
              </Button>
            </div>

            <div style={ticketModalBrandHeaderStyle} className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <img src={UAGLogo} alt="UAG" style={{ width: "56px", height: "auto" }} />
                <div>
                  <h3 className="mb-1 fw-normal">Weighbridge Ticket</h3>
                  <div className="text-soft text-uppercase fw-medium" style={{ letterSpacing: "0.08em" }}>
                    United African Grain
                  </div>
                </div>
              </div>
              <div className="text-soft fw-medium">
                {formatDate(activeDetailTicket?.created_on || activeDetailTicket?.time_out || activeDetailTicket?.time_in)}
              </div>
            </div>

            <div style={ticketDetailHeroStyle} className="p-4 mb-4 d-flex flex-column justify-content-between">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                <div>
                  <div className="text-soft text-uppercase fw-medium small mb-2">Reference Number</div>
                  <h4 className="mb-1">{activeTicketReferenceNo || "---"}</h4>
                  <div className="text-soft">
                    Ticket #{activeDetailTicket?.weigh_bridge_ticket_number || "---"}
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-soft text-uppercase fw-medium small mb-2">Status</div>
                  <div className={`fw-semibold fs-4 text-${getTicketStatusTone(activeTicketStatusText)}`}>
                    {formatTicketStatus(activeTicketStatusText)}
                  </div>
                  <div className="text-soft mt-2">
                    {formatDesignation(activeDetailTicket?.designation)}
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <TicketMetricCard
                  label="Tare Weight"
                  value={formatWeightValue(activeDetailTicket?.first_weight)}
                  tone="warning"
                />
              </div>
              <div className="col-md-4">
                <TicketMetricCard
                  label="Gross Weight"
                  value={formatWeightValue(activeDetailTicket?.second_weight)}
                  tone="primary"
                />
              </div>
              <div className="col-md-4">
                <TicketMetricCard
                  label="Net Weight"
                  value={formatWeightValue(activeDetailTicket?.net_weight)}
                  tone="success"
                />
              </div>
            </div>

            <div className="row g-3">
              <div className="col-lg-6">
                <TicketDetailSection
                  title="Ticket Overview"
                  subtitle={
                    ticketDetailsLoading
                      ? "Loading full weighbridge ticket details..."
                      : "Primary identifiers, workflow linkage, and sync metadata."
                  }
                >
                  {overviewFields.map((field, index) => (
                    <TicketDetailField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      isLast={index === overviewFields.length - 1}
                    />
                  ))}
                </TicketDetailSection>
              </div>
              <div className="col-lg-6">
                <TicketDetailSection
                  title="Vehicle Details"
                  subtitle="The truck, transporter, and attached trailer details captured for the ticket."
                >
                  {vehicleFields.map((field, index) => (
                    <TicketDetailField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      isLast={index === vehicleFields.length - 1}
                    />
                  ))}
                </TicketDetailSection>
              </div>
              <div className="col-lg-6">
                <TicketDetailSection
                  title="Weight Timeline"
                  subtitle="The exact tare, gross, and net figures together with weighing timestamps."
                >
                  {weightFields.map((field, index) => (
                    <TicketDetailField
                      key={field.label}
                      label={field.label}
                      value={field.value}
                      valueClassName={field.valueClassName || ""}
                      isLast={index === weightFields.length - 1}
                    />
                  ))}
                </TicketDetailSection>
              </div>
              <div className="col-lg-6">
                <TicketDetailSection
                  title={hasLinkedDocument ? "Linked Document" : "Additional Context"}
                  subtitle={
                    hasLinkedDocument
                      ? "Upstream or downstream workflow records associated with this weighbridge ticket."
                      : "Supporting information captured alongside this weighbridge ticket."
                  }
                >
                  {hasLinkedDocument ? (
                    linkedDocumentFields.map((field, index) => (
                      <TicketDetailField
                        key={`${field.label}-${index}`}
                        label={field.label}
                        value={field.value}
                        isLast={index === linkedDocumentFields.length - 1}
                      />
                    ))
                  ) : (
                    <>
                      <TicketDetailField label="Consigner Type" value={activeConsignerType?.consigner_type || "---"} />
                      <TicketDetailField label="Comments" value={activeDetailTicket?.comments || "---"} isLast />
                    </>
                  )}
                </TicketDetailSection>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </>
  );
};

export default TicketSelectorPage;
